import { isAllowedProduct, allowedCategoryIds, catalogVersion } from '@/data/catalogScope';
import type { Product, ProductVariant } from "@/mocks/products";
import type { CartItem } from "@/context/CartContext";
import { getReferralToken } from './affiliates';
import { getCartId } from './cartTracking';

interface StoreApiVariant {
  id: number;
  price: string | number | null;
  compareAtPrice?: string | number | null;
  inStock: boolean;
  stock?: number | null;
  values: Record<string, string>;
  imageId: number | null;
}

interface StoreApiProduct {
  id: number;
  name: string;
  handle: string;
  description?: string;
  price: string | number | null;
  compareAtPrice: string | number | null;
  image: string | null;
  images: string[];
  imageIds?: number[];
  inStock: boolean;
  categories: number[];
  variantId: number | null;
  attributes?: Record<string, string[]>;
  variants?: StoreApiVariant[];
}

export interface StoreCategory {
  id: string;
  label: string;
}

const configuredApi = (import.meta.env.VITE_STORE_API_URL as string | undefined)?.replace(/\/$/, "");
const API_BASE = import.meta.env.DEV ? "/store-api" : (configuredApi || "/server-loja");

export function isStoreEnabled(): boolean {
  return Boolean(API_BASE);
}

// O HostGator às vezes intercepta a primeira requisição de uma sessão com um
// desafio anti-bot (responde com um <script> que só seta um cookie e recarrega
// a página). Um fetch() nunca executa esse script, então resolvemos manualmente:
// setamos o cookie indicado e repetimos a chamada uma vez.
async function fetchPastBotChallenge(url: string, init?: RequestInit): Promise<Response> {
  const response = await fetch(url, init);
  const text = await response.clone().text();
  const challenge = text.match(/document\.cookie\s*=\s*"([^"]+)"/);
  if (challenge) {
    document.cookie = challenge[1];
    return fetch(url, init);
  }
  return response;
}

function findAttributeValues(attributes: Record<string, string[]> | undefined, ...names: string[]): string[] {
  if (!attributes) return [];
  const lowerNames = names.map((n) => n.toLowerCase());
  for (const [key, values] of Object.entries(attributes)) {
    if (lowerNames.includes(key.toLowerCase())) return values;
  }
  return [];
}

function toProduct(item: StoreApiProduct): Product {
  const price = Number(item.price ?? 0);
  const colors = findAttributeValues(item.attributes, "cor", "color");
  const materials = findAttributeValues(item.attributes, "material");
  const variants: ProductVariant[] | undefined = item.variants?.map((v) => ({
    id: v.id,
    price: Number(v.price ?? 0),
    compareAtPrice: v.compareAtPrice != null ? Number(v.compareAtPrice) : undefined,
    inStock: v.inStock,
    stock: v.stock ?? null,
    values: v.values,
    imageId: v.imageId ?? undefined,
  }));
  return {
    id: item.id,
    name: item.name,
    price,
    compareAtPrice: item.compareAtPrice != null ? Number(item.compareAtPrice) : undefined,
    installments: 12,
    installmentValue: price / 12,
    image: item.image ?? "",
    images: item.images,
    imageIds: item.imageIds,
    description: item.description,
    inStock: item.inStock,
    color: colors[0] ?? "",
    material: materials[0] ?? "",
    variantId: item.variantId ?? undefined,
    categoryIds: item.categories,
    attributes: item.attributes,
    variants,
  };
}

const MAX_PER_PAGE = 100;

async function fetchProductsPage(params: { categoryId?: string; page: number; productId?: string; perPage?: number; sortBy?: string }): Promise<{ items: StoreApiProduct[]; hasMore: boolean }> {
  const search = new URLSearchParams({ scope: catalogVersion });
  if (params.productId) search.set("product_id", params.productId);
  if (params.categoryId) search.set("category_id", params.categoryId);
  if (params.sortBy) search.set("sort_by", params.sortBy);
  search.set("page", String(params.page));
  search.set("per_page", String(params.perPage ?? MAX_PER_PAGE));

  const response = await fetchPastBotChallenge(`${API_BASE}/products.php?${search.toString()}`);
  if (!response.ok) {
    throw new Error(`Falha ao buscar produtos da loja (HTTP ${response.status})`);
  }

  const data = await response.json();
  if (!Array.isArray(data.products)) throw new Error("Resposta inválida do catálogo.");
  return { items: (data.products as StoreApiProduct[]).filter(item=>isAllowedProduct(item.id)), hasMore: data.has_more === true };
}

export async function fetchStoreProducts(params: { categoryId?: string; page?: number; productId?: string } = {}): Promise<Product[]> {
  if (!API_BASE) return [];

  // Um único produto: busca direta, sem paginar.
  if (params.productId) {
    const { items } = await fetchProductsPage({ productId: params.productId, page: 1 });
    return items.filter((item) => String(item.id) === params.productId).map(toProduct);
  }

  // Uma página específica foi pedida: respeita, sem buscar o resto.
  if (params.page) {
    const { items } = await fetchProductsPage({ categoryId: params.categoryId, page: params.page });
    return items.map(toProduct);
  }

  // Catálogo completo: a filtragem (categoria, cor, preço) acontece no
  // navegador, então busca todas as páginas até a loja inteira estar carregada.
  const all: StoreApiProduct[] = [];
  for (let page = 1; page <= 50; page++) {
    const { items, hasMore } = await fetchProductsPage({ categoryId: params.categoryId, page });
    all.push(...items);
    if (!hasMore) break;
  }
  return all.map(toProduct);
}

// Os mais vendidos da loja (só os à venda). Usa a mesma consulta, e o mesmo cache do
// servidor, das sugestões da página do produto.
export async function fetchBestSellers(limit = 12): Promise<Product[]> {
  if (!API_BASE) return [];
  const { items } = await fetchProductsPage({ page: 1, perPage: SUGGESTED_FETCH_SIZE, sortBy: "best-selling" });
  return items.filter((item) => item.inStock !== false).slice(0, limit).map(toProduct);
}

// Catálogo completo guardado por 5 min no navegador: a página inicial e o catálogo
// usam a mesma lista, sem baixar tudo de novo a cada troca de página.
let catalogCache: { at: number; request: Promise<Product[]> } | null = null;
export function fetchCatalogCached(): Promise<Product[]> {
  if (catalogCache && Date.now() - catalogCache.at < 300_000) return catalogCache.request;
  const request = fetchStoreProducts().catch((error) => {
    if (catalogCache?.request === request) catalogCache = null;
    throw error;
  });
  catalogCache = { at: Date.now(), request };
  return request;
}

// Produtos mais vendidos pra sugerir na página do produto, excluindo o que
// já está sendo visto. Busca só o necessário (não o catálogo inteiro).
// per_page precisa ser igual ao usado em server-loja/warm-cache.php, pra
// essa consulta sair do cache aquecido. Sobra margem pra descartar o produto
// atual e os indisponíveis e ainda completar a lista.
const SUGGESTED_FETCH_SIZE = 12;
export async function fetchSuggestedProducts(excludeId: string | number, limit = 4): Promise<Product[]> {
  if (!API_BASE) return [];
  const { items } = await fetchProductsPage({ page: 1, perPage: SUGGESTED_FETCH_SIZE, sortBy: "best-selling" });
  return items
    .filter((item) => String(item.id) !== String(excludeId) && item.inStock !== false)
    .slice(0, limit)
    .map(toProduct);
}

// O cabeçalho (todas as páginas) e a vitrine usam a mesma lista: uma só consulta
// por visita. Se falhar, a próxima tentativa busca de novo.
let categoriesRequest: Promise<StoreCategory[]> | null = null;
export function fetchStoreCategories(): Promise<StoreCategory[]> {
  if (!categoriesRequest) {
    categoriesRequest = requestStoreCategories().catch((error) => {
      categoriesRequest = null;
      throw error;
    });
  }
  return categoriesRequest;
}

async function requestStoreCategories(): Promise<StoreCategory[]> {
  if (!API_BASE) return [];

  const response = await fetchPastBotChallenge(`${API_BASE}/categories.php?scope=${catalogVersion}`);
  if (!response.ok) {
    throw new Error(`Falha ao buscar categorias da loja (HTTP ${response.status})`);
  }

  const data = await response.json();
  if (!Array.isArray(data.categories)) throw new Error("Resposta inválida de categorias.");
  return (data.categories as Array<{ id: number; name: string }>).filter(category=>allowedCategoryIds.has(category.id)).map((category) => ({
    id: String(category.id),
    label: category.name,
  }));
}

export async function createCheckout(items: CartItem[]): Promise<string> {
  if (!API_BASE) throw new Error("O checkout ainda não está ativo nesta prévia da loja.");

  if (items.length === 0 || items.some((item) => !isAllowedProduct(item.id) || !Number.isSafeInteger(item.id) || !Number.isSafeInteger(item.variantId) || item.variantId! <= 0 || !Number.isSafeInteger(item.quantity))) {
    throw new Error("Um item não está disponível para checkout. Remova-o e adicione novamente pela vitrine.");
  }

  const response = await fetchPastBotChallenge(`${import.meta.env.DEV ? "/local-affiliates" : API_BASE}/checkout.php`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      affiliate_token: await getReferralToken(),
      cart_id: getCartId(),
      items: items.map((item) => ({ product_id: item.id, variant_id: item.variantId, quantity: item.quantity })),
    }),
  });

  if (!response.ok) {
    const details = await response.json().catch(() => null);
    throw new Error(details?.message ?? `Falha ao criar o checkout (HTTP ${response.status})`);
  }

  const data = await response.json();
  if (typeof data.checkout_url !== "string") throw new Error("O checkout não retornou um endereço de pagamento.");
  const url = new URL(data.checkout_url);
  if (url.protocol !== "https:" || url.username || url.password) throw new Error("Endereço de checkout inválido.");
  return url.href;
}
